import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card, { CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import { paymentsService } from '../../../services/paymentsService';

const PaymentDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [payment, setPayment] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    const res = await paymentsService.get(id);
    setPayment(res.payment);
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const requestRefund = async () => {
    if (!payment || payment.status !== 'COMPLETED') return;
    const within30Days = (Date.now() - new Date(payment.createdAt).getTime()) <= 30 * 24 * 60 * 60 * 1000;
    if (!within30Days) { alert('Refunds are available within 30 days of payment.'); return; }
    if (!confirm('Request a refund for this payment?')) return;
    await paymentsService.refund(payment.processorId);
    await load();
  };

  const openInvoice = () => {
    if (!payment) return;
    window.open(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402'}/api/payments/${payment.id}/invoice`, '_blank');
  };

  const downloadInvoice = async () => {
    if (!payment) return;
    await paymentsService.downloadInvoice(payment.id);
  };

  if (loading) {
    return <div className="max-w-4xl mx-auto px-4 py-8">Loading…</div>;
  }

  if (!payment) {
    return <div className="max-w-4xl mx-auto px-4 py-8">Payment not found.</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Payment Details</h1>
        <Button variant="outline" onClick={() => navigate(-1)}>Back</Button>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-600">Payment ID</div>
              <div className="font-medium text-gray-900">{payment.id}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Date</div>
              <div className="font-medium text-gray-900">{new Date(payment.createdAt).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Amount</div>
              <div className="font-medium text-gray-900">{payment.currency} {(payment.totalAmount ?? payment.amount).toFixed(2)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Status</div>
              <div className="font-medium text-gray-900">{payment.status}</div>
            </div>
            {payment.taxAmount != null && (
              <div>
                <div className="text-sm text-gray-600">Tax</div>
                <div className="font-medium text-gray-900">{payment.currency} {payment.taxAmount.toFixed(2)} {payment.taxCountry ? `(${payment.taxCountry})` : ''}</div>
              </div>
            )}
            {payment.membership?.tier?.name && (
              <div>
                <div className="text-sm text-gray-600">Description</div>
                <div className="font-medium text-gray-900">Membership: {payment.membership.tier.name}</div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button variant="outline" onClick={openInvoice}>View Invoice</Button>
            <Button variant="outline" onClick={downloadInvoice}>Download Invoice</Button>
            {payment.status === 'COMPLETED' && (
              <Button variant="danger" onClick={requestRefund}>Request Refund</Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentDetailsPage;



